import React, { useState, useEffect } from 'react';
import axios from '../../../services/api';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { generateUUID } from '../../../services/utils';
import { useTranslation } from 'react-i18next';

const CreateContractModal = ({ isOpen, onClose, community, contract, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    contractId: generateUUID(),
    startDate: '',
    endDate: '',
    paymentDate: '',
    paymentAmount: '',
  });

  useEffect(() => {
    if (contract) {
      setFormData({
        ...contract,
        startDate: contract.startDate.split('T')[0],
        endDate: contract.endDate.split('T')[0],
        paymentDate: contract.paymentDate.split('T')[0],
      });
    } else {
      setFormData({
        contractId: generateUUID(),
        startDate: '',
        endDate: '',
        paymentDate: '',
        paymentAmount: '',
      });
    }
  }, [contract]);

  const handleSubmit = async e => {
    e.preventDefault();

    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      toast.error(t('Start date must be before end date'));
      return;
    }

    if (formData.paymentAmount <= 0) {
      toast.error(t('Payment amount must be greater than 0'));
      return;
    }

    try {
      if (contract) {
        await axios.put(`/contracts/${contract._id}`, { ...formData, communityId: community._id });
        toast.success(t('Contract updated successfully'));
      } else {
        await axios.post(`/contracts`, { ...formData, communityId: community._id });
        toast.success(t('Contract created successfully'));
      }
      setFormData({
        contractId: generateUUID(),
        startDate: '',
        endDate: '',
        paymentDate: '',
        paymentAmount: '',
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[15%] pt-[7%] z-[60] app-text">
      <div className="bg-white w-[30vw] h-[65vh] rounded-lg shadow-xl relative overflow-hidden">
        <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
          <h2 className="text-lg font-semibold">
            {contract ? t('Edit Contract') : t('Create New Contract')}
          </h2>
          <button onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 h-[calc(65vh-130px)] overflow-y-auto">
          <div className="space-y-4">
            <div>
              <label className="block mb-1">{t('Contract ID')}</label>
              <input
                type="text"
                value={formData.contractId}
                onChange={e => setFormData({ ...formData, contractId: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-1.5"
                disabled
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">{t('Start Date')}</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5"
                  required
                />
              </div>
              <div>
                <label className="block mb-1">{t('End Date')}</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">{t('Payment Date')}</label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={e => setFormData({ ...formData, paymentDate: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5"
                  required
                />
              </div>
              <div>
                <label className="block mb-1">{t('Payment Amount')}</label>
                <input
                  type="number"
                  value={formData.paymentAmount}
                  onChange={e => setFormData({ ...formData, paymentAmount: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5"
                />
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-1.5 border border-gray-300 rounded"
              >
                {t('Cancel')}
              </button>
              <button type="submit" className="px-6 py-1.5 border border-gray-300 rounded">
                {contract ? t('Update') : t('Save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateContractModal;
